import { Button } from '@affine/component/ui/button';
import { Checkbox } from '@affine/component/ui/checkbox';
import { Modal } from '@affine/component/ui/modal';
import { DocCreated, DocsService } from '@affine/core/modules/doc';
import {
  StreamLainDisclosure,
  StreamLainDisclosureStyles,
} from '@affine/core/modules/streamlain/native-app/streamlain-disclosure';
import { WorkbenchService } from '@affine/core/modules/workbench';
import { type I18nFuncs, useI18n } from '@affine/i18n';
import {
  CRASH_DIAGNOSTICS_NOTICE_VERSION,
  normalizeTelemetryConsent,
  type TelemetryConsent,
} from '@affine/track';
import { useLiveData, useService } from '@toeverything/infra';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAppSettingHelper } from '../hooks/affine/use-app-setting-helper';
import * as styles from './telemetry-consent.css';

type DetailCopy = {
  included: string;
  excluded: string;
};

type Copy = {
  promptTitle: string;
  promptDescription: string;
  denyAction: string;
  allowAction: string;
  diagnostics: string;
  diagnosticsSummary: string;
  diagnosticsDescription: string;
  detailsLabel: string;
  settingsRoute: string;
  close: string;
  headings: {
    included: string;
    excluded: string;
  };
  details: DetailCopy;
};

export function createTelemetryDisclosureCopy(t: I18nFuncs): Copy {
  return {
    promptTitle: t['com.streamlain.telemetryConsent.promptTitle'](),
    promptDescription: t['com.streamlain.telemetryConsent.promptDescription'](),
    denyAction: t['com.streamlain.telemetryConsent.denyAction'](),
    allowAction: t['com.streamlain.telemetryConsent.allowAction'](),
    diagnostics: t['com.streamlain.telemetryConsent.diagnostics'](),
    diagnosticsSummary:
      t['com.streamlain.telemetryConsent.diagnosticsSummary'](),
    diagnosticsDescription:
      t['com.streamlain.telemetryConsent.diagnosticsDescription'](),
    detailsLabel: t['com.streamlain.telemetryConsent.detailsLabel'](),
    settingsRoute: t['com.streamlain.telemetryConsent.settingsRoute'](),
    close: t['com.streamlain.telemetryConsent.close'](),
    headings: {
      included: t['com.streamlain.telemetryConsent.headings.included'](),
      excluded: t['com.streamlain.telemetryConsent.headings.excluded'](),
    },
    details: {
      included: t['com.streamlain.telemetryConsent.details.included'](),
      excluded: t['com.streamlain.telemetryConsent.details.excluded'](),
    },
  };
}

export function useTelemetryDisclosureCopy(): Copy {
  return createTelemetryDisclosureCopy(useI18n());
}

export function explicitSelection(draft: TelemetryConsent): TelemetryConsent {
  return {
    ...draft,
    productAnalytics: 'denied',
    crashDiagnostics:
      draft.crashDiagnostics === 'granted' ? 'granted' : 'denied',
  };
}

function DiagnosticsDisclosure({
  state,
  onChange,
  copy,
  showSettingsRoute,
}: {
  state: TelemetryConsent['crashDiagnostics'];
  onChange: (granted: boolean) => void;
  copy: Copy;
  showSettingsRoute: boolean;
}) {
  const controlName = 'telemetry-consent-crashDiagnostics';

  return (
    <section className={styles.purposeGroup} data-purpose="crashDiagnostics">
      <Checkbox
        checked={state === 'granted'}
        className={styles.checkbox}
        label={copy.diagnostics}
        labelClassName={styles.checkboxLabel}
        name={controlName}
        onChange={(_, checked) => onChange(checked)}
      />
      <p className={styles.diagnosticsSummary}>{copy.diagnosticsSummary}</p>
      <StreamLainDisclosure
        className={styles.disclosure}
        summary={copy.detailsLabel}
      >
        <div className={styles.disclosureContent}>
          <p className={styles.disclosureIntro}>
            {copy.diagnosticsDescription}
          </p>
          <dl className={styles.disclosureList}>
            <dt className={styles.disclosureTerm}>{copy.headings.included}</dt>
            <dd className={styles.disclosureDescription}>
              {copy.details.included}
            </dd>
            <dt className={styles.disclosureTerm}>{copy.headings.excluded}</dt>
            <dd className={styles.disclosureDescription}>
              {copy.details.excluded}
            </dd>
          </dl>
          {showSettingsRoute ? (
            <p className={styles.settingsNote}>{copy.settingsRoute}</p>
          ) : null}
        </div>
      </StreamLainDisclosure>
    </section>
  );
}

export function TelemetryConsentForm({
  value,
  copy,
  compact = false,
  showSettingsRoute = false,
  onChange,
}: {
  value: TelemetryConsent;
  copy: Copy;
  compact?: boolean;
  showSettingsRoute?: boolean;
  onChange: (value: TelemetryConsent) => void;
}) {
  const setDiagnostics = (granted: boolean) =>
    onChange(
      explicitSelection({
        ...value,
        crashDiagnostics: granted ? 'granted' : 'denied',
      })
    );

  return (
    <div
      data-testid="telemetry-consent-settings"
      className={clsx(styles.root, compact && styles.compactRoot)}
    >
      <StreamLainDisclosureStyles />
      <DiagnosticsDisclosure
        state={value.crashDiagnostics}
        onChange={setDiagnostics}
        copy={copy}
        showSettingsRoute={showSettingsRoute}
      />
    </div>
  );
}

export function TelemetryConsentPrompt({
  copy,
  onDecision,
}: {
  copy: Copy;
  onDecision: (granted: boolean) => void;
}) {
  return (
    <div className={styles.promptRoot}>
      <StreamLainDisclosureStyles />
      <StreamLainDisclosure
        className={clsx(styles.disclosure, styles.promptDisclosure)}
        summary={copy.detailsLabel}
      >
        <div className={styles.disclosureContent}>
          <dl className={styles.disclosureList}>
            <dt className={styles.disclosureTerm}>{copy.headings.included}</dt>
            <dd className={styles.disclosureDescription}>
              {copy.details.included}
            </dd>
            <dt className={styles.disclosureTerm}>{copy.headings.excluded}</dt>
            <dd className={styles.disclosureDescription}>
              {copy.details.excluded}
            </dd>
          </dl>
          <p className={styles.settingsNote}>{copy.settingsRoute}</p>
        </div>
      </StreamLainDisclosure>
      <div className={styles.promptActions}>
        <Button onClick={() => onDecision(false)} variant="secondary">
          {copy.denyAction}
        </Button>
        <Button onClick={() => onDecision(true)} variant="primary">
          {copy.allowAction}
        </Button>
      </div>
    </div>
  );
}

export function useTelemetryConsentController() {
  const { appSettings, updateSettings } = useAppSettingHelper();
  const value = useMemo(
    () => normalizeTelemetryConsent(appSettings),
    [appSettings]
  );
  const setCrashDiagnostics = (granted: boolean) => {
    updateSettings(
      'telemetryConsent',
      explicitSelection({
        ...value,
        crashDiagnostics: granted ? 'granted' : 'denied',
        crashDiagnosticsDecision: {
          decidedAt: new Date().toISOString(),
          noticeVersion: CRASH_DIAGNOSTICS_NOTICE_VERSION,
          appVersion: BUILD_CONFIG.appVersion,
        },
      })
    );
    updateSettings('enableTelemetry', undefined);
  };

  return { value, setCrashDiagnostics };
}

/**
 * Defers the one-time diagnostics decision until a user-created document is
 * actually entered. Startup, demo/tutorial docs and background/system docs do
 * not satisfy this trigger.
 */
export function DeferredTelemetryConsentPrompt() {
  const docsService = useService(DocsService);
  const workbench = useService(WorkbenchService).workbench;
  const location = useLiveData(workbench.location$);
  const copy = useTelemetryDisclosureCopy();
  const { value, setCrashDiagnostics } = useTelemetryConsentController();
  const [pendingDocId, setPendingDocId] = useState<string | null>(null);

  useEffect(() => {
    return docsService.eventBus.on(DocCreated, event => {
      if (event.docCreateOptions.userInitiated) {
        setPendingDocId(event.doc.id);
      }
    });
  }, [docsService]);

  const activeDocId = location.pathname.split('/').filter(Boolean)[0] ?? null;
  const open =
    value.crashDiagnostics === 'uninitialized' &&
    pendingDocId !== null &&
    pendingDocId === activeDocId;

  const handleDecision = useCallback(
    (granted: boolean) => {
      setCrashDiagnostics(granted);
      setPendingDocId(null);
    },
    [setCrashDiagnostics]
  );

  if (!open) {
    return null;
  }

  return (
    <Modal
      animation="none"
      closeButtonOptions={{
        'aria-label': copy.close,
        'data-testid': 'diagnostics-consent-close',
      }}
      description={copy.promptDescription}
      onOpenChange={nextOpen => {
        if (!nextOpen) handleDecision(false);
      }}
      open
      title={copy.promptTitle}
      width={520}
    >
      <TelemetryConsentPrompt copy={copy} onDecision={handleDecision} />
    </Modal>
  );
}

export function TelemetryConsentSettings({
  compact = false,
  showSettingsRoute = false,
}: {
  compact?: boolean;
  showSettingsRoute?: boolean;
}) {
  const t = useI18n();
  const copy = createTelemetryDisclosureCopy(t);
  const { value, setCrashDiagnostics } = useTelemetryConsentController();

  return (
    <TelemetryConsentForm
      value={value}
      copy={copy}
      compact={compact}
      showSettingsRoute={showSettingsRoute}
      onChange={next =>
        setCrashDiagnostics(next.crashDiagnostics === 'granted')
      }
    />
  );
}
